import { Logger } from '../../logger/Logger';
import { initVBO } from '../stream/ambientLight/initVBO';

import BASE_VERTEX_SHADER from './shaders/BaseVertexShader.glsl?raw';
import SCREEN_FRAGMENT_SHADER from './shaders/ScreenFragmentShader.glsl?raw';

export const mirrorVideo = (video: HTMLVideoElement, canvas: HTMLCanvasElement): (() => void) | void => {
  const gl = canvas?.getContext('webgl', { antialias: true });

  if (!gl) return;

  //Configuration
  gl.enable(gl.CULL_FACE);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  initVBO(gl);

  const initTexture = (gl: WebGLRenderingContext): WebGLTexture | null => {
    const texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);

    const level = 0;
    const internalFormat = gl.RGBA;
    const width = canvas.width;
    const height = canvas.height;
    const border = 0;
    const srcFormat = gl.RGBA;
    const srcType = gl.UNSIGNED_BYTE;
    const data = new Uint8Array(width * height * 4).fill(0);
    gl.texImage2D(gl.TEXTURE_2D, level, internalFormat, width, height, border, srcFormat, srcType, data);

    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);

    return texture;
  };

  const videoTexture = initTexture(gl);

  if (!videoTexture) {
    return;
  }

  // Create our vertex shader
  const VS = gl.createShader(gl.VERTEX_SHADER);
  if (!VS) return;
  gl.shaderSource(VS, BASE_VERTEX_SHADER);
  gl.compileShader(VS);

  const screenFS = gl.createShader(gl.FRAGMENT_SHADER);
  if (!screenFS) return;
  gl.shaderSource(screenFS, SCREEN_FRAGMENT_SHADER);
  gl.compileShader(screenFS);

  const screenProgram = gl.createProgram();
  if (!screenProgram) return;
  gl.attachShader(screenProgram, VS);
  gl.attachShader(screenProgram, screenFS);
  gl.linkProgram(screenProgram);

  const screenPositionLocation = gl.getAttribLocation(screenProgram, 'position');
  gl.vertexAttribPointer(screenPositionLocation, 2, gl.FLOAT, false, 8, 0);
  gl.enableVertexAttribArray(screenPositionLocation);

  gl.useProgram(screenProgram);
  gl.uniform1i(gl.getUniformLocation(screenProgram, 'screen'), 1);

  gl.activeTexture(gl.TEXTURE1);
  gl.bindTexture(gl.TEXTURE_2D, videoTexture);

  const level = 0;
  const internalFormat = gl.RGBA;
  const srcFormat = gl.RGBA;
  const srcType = gl.UNSIGNED_BYTE;
  gl.viewport(0, 0, canvas.width, canvas.height);

  let failedReadingTextureFromVideo = false;

  return (): void => {
    if (failedReadingTextureFromVideo) return;

    if (video && !video.paused) {
      try {
        gl.texImage2D(gl.TEXTURE_2D, level, internalFormat, srcFormat, srcType, video);
      } catch (e) {
        if (!failedReadingTextureFromVideo) {
          Logger.logFailure('errors', 'backToGameComponent', 'failedReadingTextureFromVideo', Date.now(), 1, '', false);
          failedReadingTextureFromVideo = true;
        }
      }
    }
    //render
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  };
};
